import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ContentCopy as CopyIcon,
  Email as EmailIcon,
  FilterList as FilterIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Verified as VerifiedIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAppMode } from '../contexts/AppModeContext';
import { supabase } from '../lib/supabase';

type TalentPoolStatus = 'applicant' | 'member' | 'consumer' | 'vendor';

type TalentPoolUser = {
  id: string;
  email: string;
  alias: string | null;
  first_name: string | null;
  last_name: string | null;
  avatar?: string | null;
  user_status_preference: TalentPoolStatus | string | null;
  location?: string | null;
  created_at?: string | null;
};

const pageSizeOptions = [12, 24, 48] as const;

const TalentPoolPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { is_job } = useAppMode();

  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TalentPoolStatus>(() => (is_job ? 'applicant' : 'consumer'));
  const [pageSize, setPageSize] = useState<(typeof pageSizeOptions)[number]>(24);

  const [users, setUsers] = useState<TalentPoolUser[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  const [page, setPage] = useState(1);

  const statusesAvailable: TalentPoolStatus[] = useMemo(() => {
    return is_job ? ['applicant', 'member'] : ['consumer', 'vendor'];
  }, [is_job]);

  useEffect(() => {
    setStatusFilter((prev) => {
      if (statusesAvailable.includes(prev)) return prev;
      return statusesAvailable[0];
    });
  }, [statusesAvailable]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, pageSize]);

  const visibleUsers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return users.slice(start, start + pageSize);
  }, [page, pageSize, users]);

  const maxPage = useMemo(() => {
    return Math.max(1, Math.ceil((users.length || 0) / pageSize));
  }, [pageSize, users.length]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const q = (search || '').trim();

        // Note: Some deployments use RLS. If this fails, we show a helpful toast.
        let query = supabase
          .from('users')
          .select('id, email, alias, first_name, last_name, user_status_preference, location, created_at', { count: 'exact' })
          .in('user_status_preference', [statusFilter]);

        if (q) {
          const safe = q.replace(/,/g, '');
          query = query.or(
            `alias.ilike.%${safe}%,first_name.ilike.%${safe}%,last_name.ilike.%${safe}%,email.ilike.%${safe}%`
          );
        }

        query = query.order('created_at', { ascending: false }).limit(500);

        const { data, error, count } = await query;

        if (error) {
          throw error;
        }

        const list = (data || []) as any[];

        setUsers(
          list.map((u) => ({
            id: String(u?.id || ''),
            email: String(u?.email || ''),
            alias: u?.alias ?? null,
            first_name: u?.first_name ?? null,
            last_name: u?.last_name ?? null,
            user_status_preference: (u?.user_status_preference ?? null) as any,
            location: u?.location ?? null,
            created_at: u?.created_at ?? null,
          }))
        );
        setTotalCount(typeof count === 'number' ? count : list.length);
      } catch (e: any) {
        console.error('[TalentPoolPage] load error', e);
        setUsers([]);
        setTotalCount(0);
        toast.error(String(e?.message || 'Failed to load talent pool users'));
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, [refreshKey, search, statusFilter]);

  const title = is_job ? 'Talent Pool' : 'Customer Pool';

  const statusLabel = (s: TalentPoolStatus) => {
    if (s === 'applicant') return 'Applicant';
    if (s === 'member') return 'Member';
    if (s === 'vendor') return 'Vendor';
    return 'Consumer';
  };

  const getInitials = (u: TalentPoolUser) => {
    const a = String(u?.alias || '').trim();
    if (a) return a.slice(0, 2).toUpperCase();
    const f = String(u?.first_name || '').trim();
    const l = String(u?.last_name || '').trim();
    const v = `${f.slice(0, 1)}${l.slice(0, 1)}`.trim();
    return v ? v.toUpperCase() : 'U';
  };

  const displayName = (u: TalentPoolUser) => {
    const f = String(u?.first_name || '').trim();
    const l = String(u?.last_name || '').trim();
    const full = `${f} ${l}`.trim();
    return full || (u.alias ? String(u.alias) : 'User');
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {loading && <LinearProgress />}
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <IconButton onClick={() => navigate(-1)}>
              <BackIcon />
            </IconButton>
            <Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={800}>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {is_job
                  ? 'Browse and manage applicants/members who opted into job opportunities.'
                  : 'Browse and manage consumers/vendors who opted into the marketplace.'}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap">
            <Tooltip title="Refresh">
              <IconButton onClick={() => setRefreshKey((k) => k + 1)}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => {
                const next = statusesAvailable[(statusesAvailable.indexOf(statusFilter) + 1) % statusesAvailable.length];
                setStatusFilter(next);
              }}
            >
              {statusLabel(statusFilter)}
            </Button>
          </Stack>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            gap: 2,
            mb: 2,
          }}
        >
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <TextField
                  fullWidth
                  placeholder="Search by alias, name, or email"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as TalentPoolStatus)} sx={{ minWidth: 170 }}>
                  {statusesAvailable.map((s) => (
                    <MenuItem key={s} value={s}>
                      {statusLabel(s)}
                    </MenuItem>
                  ))}
                </Select>

                <Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value) as any)} sx={{ minWidth: 120 }}>
                  {pageSizeOptions.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}/page
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack spacing={1}>
                <Typography variant="overline" color="text.secondary" fontWeight={700}>
                  Overview
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="h6" fontWeight={800}>
                    {totalCount}
                  </Typography>
                  <Chip
                    icon={<VerifiedIcon />}
                    label={statusLabel(statusFilter)}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 700 }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Showing {users.length} loaded records. Use search + filter to narrow down.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              Page {page} / {maxPage}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="outlined" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Prev
              </Button>
              <Button variant="outlined" disabled={page >= maxPage} onClick={() => setPage((p) => Math.min(maxPage, p + 1))}>
                Next
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
              gap: 2,
            }}
          >
            {Array.from({ length: 8 }).map((_, idx) => (
              <Card key={idx} sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Skeleton variant="circular" width={44} height={44} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton width="70%" />
                        <Skeleton width="50%" />
                      </Box>
                    </Stack>
                    <Skeleton width="40%" />
                    <Skeleton width="90%" />
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : visibleUsers.length === 0 ? (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={800}>
                No users found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try changing the status filter or search keyword.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
              gap: 2,
            }}
          >
            {visibleUsers.map((u) => (
              <Card
                key={u.id}
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  transition: 'transform 120ms ease, box-shadow 120ms ease',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44 }}>
                        {getInitials(u)}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={800} noWrap>
                          {displayName(u)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {u.alias ? `@${u.alias}` : u.email}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={String(u.user_status_preference || statusFilter)}
                        sx={{ textTransform: 'capitalize', fontWeight: 700 }}
                        color={String(u.user_status_preference) === 'vendor' ? 'primary' : 'default'}
                      />
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Chip
                        size="small"
                        icon={<VisibilityIcon />}
                        label="Profile"
                        variant="outlined"
                        onClick={() => navigate(`/user-profile/${u.id}`)}
                      />
                      <Chip
                        size="small"
                        icon={<OpenInNewIcon />}
                        label="Open"
                        variant="outlined"
                        onClick={() => navigate(`/user-profile/${u.id}`)}
                      />
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {u.location ? String(u.location) : '—'}
                      </Typography>

                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title="Copy email">
                          <IconButton
                            size="small"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(u.email);
                                toast.success('Email copied');
                              } catch {
                                toast.error('Failed to copy');
                              }
                            }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Send email">
                          <IconButton
                            size="small"
                            onClick={() => {
                              try {
                                window.location.href = `mailto:${u.email}`;
                              } catch {
                                toast.error('Unable to open mail client');
                              }
                            }}
                          >
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default TalentPoolPage;
